<template>
  <WdsPageSignup class="wds-page-signup-login">
    <template #left>
      <LeftWelcome />
    </template>

    <template #nav-left>
      <img src="/gfx/worksome-logo-black.svg" alt="Worksome" class="tw-h-6 tw-block md:tw-hidden" />
    </template>

    <template #nav-right>
      <AuthLanguageSwitcher />
    </template>

    <template #right>
      <WdsHeading as="h1" type="title1">
        {{ $t('Log in to Worksome') }}
      </WdsHeading>
      <WdsText large secondary>
        {{ $t('Please enter your details to access your account') }}
      </WdsText>

      <WdsAlert v-if="tokenError !== null" error class="tw-mt-4">
        {{ tokenError }}
      </WdsAlert>

      <WdsButtonSocial class="!tw-mt-7" linkedin @click="onLoginSocial('linkedin')">
        {{ $t('Log in with LinkedIn') }}
      </WdsButtonSocial>
      <WdsButtonSocial class="!tw-mt-6" google @click="onLoginSocial('google')">
        {{ $t('Log in with Google') }}
      </WdsButtonSocial>

      <WdsDivider class="!tw-mt-7 !tw-mb-7 !tw-text-gray-600 tw-text-base">
        {{ $t('Or') }}
      </WdsDivider>

      <BaseHtmlForm action="/login" method="POST" novalidate @form-submitted="onFormSubmitted">
        <input name="redirect_uri" type="hidden" value="" />
        <WdsInput
          v-model="email"
          :error="errorEmail"
          :label="$t('Email')"
          lg
          name="email"
          :placeholder="$t('example@company.com')"
          autocomplete="email"
          autofocus
          required
          type="email"
          data-test="sign-up-email-input"
          @input="clearErrors"
        />

        <div class="!tw-mt-6">
          <WdsInput
            v-model="password"
            :error="errorPassword"
            :label="$t('Password')"
            lg
            name="password"
            :placeholder="$t('minimum 8 characters')"
            autocomplete="current-password"
            required
            type="password"
            data-test="sign-up-password-input"
            @input="clearErrors"
          />
        </div>

        <div class="remember-field">
          <WdsStack justify-space-between>
            <WdsCheckbox
              v-model:checked="remember"
              :label="$t('Remember me')"
              name="remember"
              data-test="sign-up-remember-me"
            />

            <div class="tw-text-base">
              <a
                href="/forgot-password"
                class="hover:tw-underline"
                data-test="sign-up-forgot-password-link"
              >
                {{ $t('Forgot your password?') }}
              </a>
            </div>
          </WdsStack>
        </div>

        <WdsButton
          class="!tw-mt-6 no-spinner"
          full-width
          lg
          primary
          type="submit"
          data-test="log-in-submit"
          :disabled="email === '' || !!errorEmail"
          @click="onClickSubmit"
        >
          {{ $t('Log in') }}
        </WdsButton>
      </BaseHtmlForm>

      <div class="login-bottom-links">
        <div class="!tw-mt-3">
          <a
            href="/login/saml"
            class="tw-text-gray-600 hover:tw-underline"
            data-test="sign-up-saml-login-link"
          >
            {{ $t('Log in with SAML SSO') }}
          </a>
        </div>
        <hr class="tw-my-3" />
        <div class="tw-text-gray-600" style="font-weight: 500">
          {{ $t('Does your company use Worksome and you need a login?') }}
          <a href="#" class="intercomchat hover:tw-underline" data-test="register-chat-button">
            {{ $t('Chat with us') }}
          </a>
        </div>
        <div class="!tw-mt-1 tw-text-gray-600" style="font-weight: 500">
          {{ $t('Ready to get started with Worksome at your company?') }}
          <a
            href="https://www.worksome.com/demo"
            class="hover:tw-underline"
            data-test="register-chat-button"
          >
            {{ $t('Contact us') }}
          </a>
        </div>
        <WdsAlert info class="tw-mt-3">
          <template #description>
            <WdsText secondary>
              {{
                $t(
                  'Currently, Worksome is invite-only for freelancers and independent talent, and does not offer general public access to the platform.',
                )
              }}
            </WdsText>
          </template>
        </WdsAlert>
      </div>
    </template>
  </WdsPageSignup>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import { email as emailValidator, helpers, required } from '@vuelidate/validators'
import {
  WdsAlert,
  WdsButton,
  WdsButtonSocial,
  WdsCheckbox,
  WdsDivider,
  WdsHeading,
  WdsInput,
  WdsPageSignup,
  WdsText,
  WdsStack,
} from '@worksome/wds-components'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import AuthLanguageSwitcher from '@/components/auth/AuthLanguageSwitcher.vue'
import LeftWelcome from '@/components/auth/left/LeftWelcome.vue'
import { $t } from '@/composables/translation'

interface Props {
  errors: any
}

const props = defineProps<Props>()

const email: Ref<string> = ref('')
const password: Ref<string> = ref('')
const remember: Ref<boolean> = ref(false)
const backendEmailError: Ref<string | null> = ref(null)
const backendPasswordError: Ref<string | null> = ref(null)
const tokenError: Ref<string | null> = ref(null)

if (props.errors.hasOwnProperty('email')) {
  backendEmailError.value = props.errors.email[0]
}

if (props.errors.hasOwnProperty('password')) {
  backendPasswordError.value = props.errors.password[0]
}

if (props.errors.hasOwnProperty('token')) {
  tokenError.value = props.errors.token[0]
}

const rules = computed(() => ({
  email: {
    required: helpers.withMessage($t('Please enter your email address'), required),
    email: helpers.withMessage($t('Please enter your email address'), emailValidator),
    $lazy: true,
  },
  password: {
    required: helpers.withMessage($t('Remember to type in the password to your account'), required),
  },
}))

const v$ = useVuelidate(rules, { email, password })

const errorEmail = computed(() => {
  const { $dirty, $errors, $invalid } = v$.value.email
  if (!$dirty && props.errors.hasOwnProperty('email')) {
    return backendEmailError.value ?? undefined
  }
  if ($dirty && $invalid) {
    return $errors[0].$message as string
  }
  return undefined
})

const errorPassword = computed(() => {
  const { $dirty, $errors, $invalid } = v$.value.password
  if (!$dirty && props.errors.hasOwnProperty('password')) {
    return backendPasswordError.value ?? undefined
  }
  if ($dirty && $invalid) {
    return $errors[0].$message as string
  }
  return undefined
})

const onClickSubmit = () => {
  v$.value.$touch()
}

const onFormSubmitted = (event: Event) => {
  if (v$.value.$invalid) {
    event.preventDefault()
  }
}

const onLoginSocial = (provider: string) => {
  window.location.href = `/login/${provider}?login=1`
}

const clearErrors = () => {
  // clear backend errors
  backendEmailError.value = null
  backendPasswordError.value = null
  tokenError.value = null

  // clear form errors
  v$.value.email.$reset()
  v$.value.password.$reset()
}
</script>

<style lang="scss" scoped>
a {
  @apply tw-font-medium;
}

.wds-input-wrapper > .wds-input-label {
  margin-bottom: unset;
}

.remember-field {
  @apply tw-mt-6;
}

.login-bottom-links {
  @apply tw-text-base;
  @apply tw-pt-1;
}
</style>
